// 소비 아이템·보석 창 DOM(D2c): 잡화상 「물약·주문서」·「연금」 탭, 가방 「소비」 탭, 대장간 「보석」 탭.
// RpgUi 창 본문 영역에만 그린다(플레이 화면 위 팝업 없음). 표시 값은 consumeModel.ts(순수), 변경은 GameCommand로만 보낸다.
// 스타일은 GearPanel이 넣은 .dh-g-* 를 같이 쓴다. 값이 바뀔 때만 다시 그린다(RpgUi가 gear.revision·version으로 판단).
import type { GameCommand, GameView } from '../shared/types';
import { FONT, FONT_NUM, el } from './dom';
import { makeRequestId } from './rpgModel';
import {
  alchemyLockText, alchemyRows, carryRows, elixirRows, gemInventory, gemLockText, gemRuleText, shopRows, socketItems, socketText,
} from './consumeModel';
import { GEM_COLORS, gemColorOf, unsocketFee } from '../shared/content/consumables';
import { itemIcon } from './itemIconDom';
import { consIconPath } from './itemIcons';
import { gearCard } from './gearModel';
import { DEFAULT_BODY_W, forgeSplit, socketSize } from './shopLayout';
import { gearThumb, priceTag, productRow, socketPip } from './shopUi';

const num = (v: number): string => v.toLocaleString('ko-KR');

export class ConsumePanel {
  /** 내부 선택이 바뀌면 오른다(RpgUi 재그리기 키) */
  version = 0;
  /** 보석 탭: 고른 장비 uid */
  private sel: string | null = null;
  /** 본문 안쪽 폭(설계 px, RpgUi가 그릴 때마다 넣는다)과 세로 화면 여부 — 카드 배치(shopLayout) 입력 */
  bodyW = DEFAULT_BODY_W;
  narrow = false;

  constructor(private readonly send: (c: GameCommand) => void) {}

  open(): void { this.sel = null; this.version++; }

  private btn(parent: HTMLElement, label: string, onClick: () => void, o: { primary?: boolean; disabled?: boolean; testid?: string } = {}): HTMLButtonElement {
    const b = el('button', '', parent, label);
    b.type = 'button';
    b.className = 'dh-g-btn' + (o.primary ? ' pri' : '');
    b.dataset.sfx = 'event';
    if (o.testid) { b.dataset.testid = o.testid; b.dataset.focusKey = o.testid; }
    if (o.disabled) b.disabled = true;
    b.addEventListener('click', onClick);
    return b;
  }
  private root(body: HTMLElement, testid: string): HTMLDivElement {
    const r = el('div', '', body);
    r.className = 'dh-g';
    r.dataset.testid = testid;
    return r;
  }
  private head(parent: HTMLElement, text: string, small = ''): void {
    const h = el('div', '', parent);
    h.className = 'dh-g-h';
    h.style.flexWrap = 'wrap';
    h.style.gap = '2px 10px';
    el('span', 'white-space:nowrap;', h, text);
    if (small) el('small', 'flex:1 1 220px;min-width:0;', h, small);
  }

  /** 잡화상 「물약·주문서」: 물약 6종(소 ①·중 ③·대 ⑤ 판매)·귀환서·부활 깃털. 1개·10개 사기 */
  renderShop(body: HTMLElement, view: GameView, gold: number, town: number, locked: boolean): void {
    const root = this.root(body, 'cons-shop');
    this.head(root, '물약 · 주문서', '퀵슬롯 R(HP) · T(MP) · 공용 쿨다운 6초 · 연속 사용마다 회복 −20%(10초 뒤 원래대로)');
    const list = el('div', '', root);
    list.className = 'dh-g-list';
    list.style.maxHeight = 'none';
    for (const r of shopRows(view.gear, town, gold, locked)) {
      const pr = productRow(list, this.bodyW, { testid: 'cons-shop-' + r.id, color: '#f2c14e', iconPath: consIconPath(r.id), buyW: 124, tag: r.owned > 0 ? '×' + r.owned : undefined });
      el('div', 'font:900 16px ' + FONT + ';color:#fff;', pr.text, r.name);
      el('div', 'font:700 12px ' + FONT + ';color:#aeb9da;line-height:1.35;', pr.text, r.desc + ' · 보유 ' + r.owned);
      if (r.price === null) { el('div', 'font:800 12px ' + FONT + ';color:#ffb37a;', pr.buy, r.lockText); continue; }
      priceTag(pr.buy, r.price, 24);
      const btns = el('div', 'display:flex;gap:6px;', pr.buy);
      Object.assign(this.btn(btns, '1개', () => this.send({ type: 'consBuy', item: r.id, n: 1, requestId: makeRequestId() }), { primary: true, disabled: !r.can1, testid: 'cons-buy1-' + r.id }).style, { flex: '1', minWidth: '52px', padding: '6px 8px' });
      Object.assign(this.btn(btns, '10개', () => this.send({ type: 'consBuy', item: r.id, n: 10, requestId: makeRequestId() }), { disabled: !r.can10, testid: 'cons-buy10-' + r.id }).style, { flex: '1', minWidth: '52px', padding: '6px 8px' });
    }
  }

  /** 잡화상 「연금」(연금술사 NPC 전까지): 비약 제작 — 마을④부터 */
  renderAlchemy(body: HTMLElement, view: GameView, gold: number, town: number, locked: boolean): void {
    const root = this.root(body, 'cons-alchemy');
    this.head(root, '연금술 · 비약 제작', '계열별 1개 · 다시 마시면 남은 시간 +30분(최대 60분) · 접속 시간 기준(오프라인 정지)');
    const rows = alchemyRows(view.gear, town, gold, locked);
    if (!rows) { el('div', '', root, alchemyLockText(town)).className = 'dh-g-warn'; return; }
    for (const r of rows) {
      const pr = productRow(root, this.bodyW, { testid: 'cons-craft-' + r.id, color: '#a45cff', iconPath: consIconPath(r.id), buyW: 124, tag: r.owned > 0 ? '×' + r.owned : undefined });
      el('div', 'font:900 16px ' + FONT + ';color:#fff;', pr.text, r.name);
      el('div', 'font:700 12px ' + FONT + ';color:#aeb9da;line-height:1.35;', pr.text, r.effect + ' · 보유 ' + r.owned);
      priceTag(pr.buy, r.price, 24);
      const btns = el('div', 'display:flex;gap:6px;', pr.buy);
      Object.assign(this.btn(btns, '제작 1', () => this.send({ type: 'consCraft', item: r.id, n: 1, requestId: makeRequestId() }), { primary: true, disabled: !r.can1, testid: 'cons-craft1-' + r.id }).style, { flex: '1', minWidth: '56px', padding: '6px 8px' });
      Object.assign(this.btn(btns, '제작 5', () => this.send({ type: 'consCraft', item: r.id, n: 5, requestId: makeRequestId() }), { disabled: !r.can5, testid: 'cons-craft5-' + r.id }).style, { flex: '1', minWidth: '56px', padding: '6px 8px' });
    }
  }

  /** 가방 「소비」: 비약(남은 시간·마시기·한 번에 사용), 물약·주문서 보유, 보석 보유 */
  renderItems(body: HTMLElement, view: GameView, locked: boolean): void {
    const root = this.root(body, 'cons-items');
    const ex = elixirRows(view.gear);
    const anyOwned = ex.some((r) => r.owned > 0);
    const h = el('div', 'display:flex;align-items:center;justify-content:space-between;gap:8px;', root);
    el('div', 'font:900 15px ' + FONT + ';color:#ffd76a;', h, '비약');
    this.btn(h, '한 번에 사용', () => this.send({ type: 'useElixir', item: 'all', requestId: makeRequestId() }), { primary: true, disabled: locked || !anyOwned, testid: 'cons-elixir-all' });
    const grid = el('div', 'display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:6px;', root);
    for (const r of ex) {
      const card = el('div', 'border-radius:8px;border:1.5px solid ' + (r.left > 0 ? '#7dffa0' : 'rgba(174,185,218,.35)') + ';background:rgba(10,14,30,.75);padding:6px 8px;display:flex;flex-direction:column;gap:3px;', grid);
      card.dataset.testid = 'cons-elixir-' + r.fam;
      const top = el('div', 'display:flex;align-items:center;gap:8px;', card);
      itemIcon(top, consIconPath(r.id), 40, r.left > 0 ? '#7dffa0' : undefined, false);
      el('div', 'font:900 14px ' + FONT + ';color:#fff;', top, r.name + ' ×' + r.owned);
      el('div', 'font:700 12px ' + FONT + ';color:#aeb9da;', card, r.effect + ' · ' + r.leftText);
      this.btn(card, r.full ? '최대(60분)' : '마시기', () => this.send({ type: 'useElixir', item: r.id, requestId: makeRequestId() }), { disabled: locked || r.owned < 1 || r.full, testid: 'cons-drink-' + r.fam });
    }
    el('div', 'font:900 15px ' + FONT + ';color:#ffd76a;margin-top:4px;', root, '물약 · 주문서');
    // 물약·주문서 보유: 그림 칸 + 수(글자만이던 줄을 그림 줄로)
    const carry = el('div', 'display:flex;flex-wrap:wrap;gap:6px;', root);
    carry.dataset.testid = 'cons-carry';
    for (const r of carryRows(view.gear)) {
      const c = el('div', 'display:flex;align-items:center;gap:6px;padding:3px 8px 3px 3px;border-radius:9px;background:rgba(10,14,30,.7);border:1px solid rgba(174,185,218,.25);' + (r.owned > 0 ? '' : 'opacity:.55;'), carry);
      itemIcon(c, consIconPath(r.id), 34, undefined, false);
      el('span', 'font:800 12.5px ' + FONT + ';color:#e6ecff;', c, r.name + ' ' + r.owned);
    }
    el('div', '', root, '물약은 던전에서 퀵슬롯(R·T 키, 터치는 회피 버튼 왼쪽)으로 마십니다. 자동 물약 문턱은 설정에서 고릅니다. 부활 깃털은 쓰러질 때 판마다 1회 자동, 귀환서는 일시정지 창에서 씁니다.').className = 'dh-g-note';
    const gems = gemInventory(view.gear, view.gear.town, 0, true);
    el('div', 'font:900 15px ' + FONT + ';color:#ffd76a;margin-top:4px;', root, '보석');
    el('div', 'font:700 13px ' + FONT + ';color:#e6ecff;', root, gems.length ? gems.map((g) => g.name + ' ' + g.count).join(' · ') : '없음(지역3부터 엘리트·보스가 떨어뜨립니다)');
  }

  /** 대장간 「보석」(보석상 NPC 전까지): 소켓 장비 고르기 → 칸마다 박기·빼기, 보석 합성 */
  renderGems(body: HTMLElement, view: GameView, gold: number, town: number, locked: boolean): void {
    const root = this.root(body, 'gem-forge');
    const lock = gemLockText(town);
    if (lock) { el('div', '', root, lock).className = 'dh-g-warn'; return; }
    el('div', '', root, gemRuleText(town)).className = 'dh-g-note';
    const items = socketItems(view.gear);
    if (this.sel && !items.some((r) => r.uid === this.sel)) this.sel = null;
    if (!this.sel && items[0]) this.sel = items[0].uid;
    const inv = gemInventory(view.gear, town, gold, locked);
    const lay = forgeSplit(this.bodyW, this.narrow);
    const split = el('div', '', root);
    split.className = 'dh-g-split';
    split.style.gridTemplateColumns = lay.stacked ? '1fr' : lay.leftW + 'px minmax(0,1fr)';
    const sk = socketSize(this.bodyW);
    const cardOf = (uid: string) => { const it = view.gear.items.find((x) => x.uid === uid); return it ? gearCard(it, Object.values(view.gear.equipped).includes(uid), view.heroId ?? 'kael') : null; };
    // 왼쪽: 소켓 장비 목록(장비 그림 + 이름 + 소켓 그림 칸)
    const left = el('div', 'min-width:0;', split);
    el('div', '', left, '소켓 장비').className = 'dh-g-h';
    const list = el('div', '', left);
    list.className = 'dh-g-list';
    list.dataset.testid = 'gem-item-list';
    if (!items.length) el('div', '', list, '소켓이 있는 장비가 없습니다(희귀 1·영웅·세트 2·전설 3칸).').className = 'dh-g-note';
    for (const r of items) {
      const c = cardOf(r.uid);
      const b = el('button', '--gc:' + r.color + ';display:flex;align-items:center;gap:8px;min-height:64px;padding:5px 8px;', list);
      b.type = 'button';
      b.className = 'dh-g-cell' + (r.uid === this.sel ? ' sel' : '');
      b.dataset.testid = 'gem-item-' + r.uid;
      b.dataset.focusKey = 'gem-item-' + r.uid;
      if (c) gearThumb(b, c, 52);
      const t = el('div', 'min-width:0;flex:1;display:flex;flex-direction:column;gap:3px;', b);
      el('span', 'color:#fff;font:900 13px ' + FONT + ';white-space:nowrap;overflow:hidden;text-overflow:ellipsis;', t, r.name);
      const pips = el('div', 'display:flex;gap:4px;', t);
      for (const code of r.sockets) socketPip(pips, code, 26);
      b.addEventListener('click', () => { if (this.sel !== r.uid) { this.sel = r.uid; this.version++; } });
    }
    // 오른쪽: 고른 장비(큰 그림) + 소켓 줄 + 가진 보석·합성
    const right = el('div', 'display:flex;flex-direction:column;gap:8px;min-width:0;', split);
    const cur = items.find((r) => r.uid === this.sel);
    if (cur) {
      const cc = cardOf(cur.uid);
      const card = el('div', '--gc:' + cur.color, right);
      card.className = 'dh-g-card';
      card.dataset.testid = 'gem-sockets';
      const head = el('div', 'display:flex;gap:10px;align-items:center;', card);
      if (cc) gearThumb(head, cc, 84).dataset.testid = 'gem-detail-icon';
      const ht = el('div', 'min-width:0;display:flex;flex-direction:column;gap:2px;', head);
      el('div', '', ht, cur.name).className = 'nm';
      el('div', '', ht, cur.sub).className = 'sub';
      cur.sockets.forEach((code, idx) => {
        const row = el('div', 'display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:4px;', card);
        row.dataset.testid = 'gem-socket-' + idx;
        socketPip(row, code, sk);
        el('span', 'flex:1 1 120px;min-width:100px;font:800 13px ' + FONT + ';color:' + (code >= 0 ? '#fff' : '#aeb9da') + ';', row, socketText(code));
        if (code >= 0) {
          const fee = unsocketFee(town);
          this.btn(row, '빼기 ' + num(fee) + 'G', () => this.send({ type: 'gemUnsocket', uid: cur.uid, idx, requestId: makeRequestId() }), { disabled: locked || gold < fee, testid: 'gem-unsocket-' + idx });
        } else {
          // 빈 칸: 가진 보석 중 하나를 바로 박는다(보석 그림 단추)
          const pick = el('div', 'display:flex;gap:6px;flex-wrap:wrap;width:100%;', row);
          for (const g of inv) {
            const b = this.btn(pick, '', () => this.send({ type: 'gemSocket', uid: cur.uid, idx, code: g.code, requestId: makeRequestId() }), { disabled: locked, testid: 'gem-socket-' + idx + '-' + g.code });
            Object.assign(b.style, { display: 'flex', alignItems: 'center', gap: '5px', minHeight: '44px', padding: '2px 8px 2px 4px', borderColor: g.color });
            b.setAttribute('aria-label', g.name + ' 박기');
            socketPip(b, g.code, 34);
            el('span', 'font:800 12px ' + FONT + ';', b, g.name);
          }
          if (!inv.length) el('span', 'font:700 12px ' + FONT + ';color:#aeb9da;', pick, '가진 보석 없음');
        }
      });
    }
    const gbox = el('div', '', right);
    el('div', '', gbox, '가진 보석 · 합성').className = 'dh-g-h';
    if (!inv.length) el('div', '', gbox, '가진 보석이 없습니다.').className = 'dh-g-note';
    for (const g of inv) {
      const row = el('div', 'display:flex;align-items:center;gap:8px;margin-top:4px;', gbox);
      row.dataset.testid = 'gem-inv-' + g.code;
      socketPip(row, g.code, 40);
      el('span', 'flex:1;min-width:0;font:800 13px ' + FONT + ';color:#fff;', row, g.name + ' ×' + g.count + ' · ' + g.effect);
      if (!g.top) this.btn(row, '합성 ' + num(g.combineFee) + 'G', () => this.send({ type: 'gemCombine', code: g.code, requestId: makeRequestId() }), { disabled: !g.canCombine, testid: 'gem-combine-' + g.code });
    }
  }
}
